import { call, put, cancelled } from "redux-saga/effects";
import { logger } from "helpers/logger";

/**
 * Creates a detailed error object from an error instance
 * @param {Error} error - The error to format
 * @returns {Object} Formatted error object with message, code, and stack
 */
export const formatSagaError = (error) => {
	return {
		message: error.message || "An unknown error occurred",
		code: error.code || error.status || null,
		name: error.name || "Error",
		stack: process.env.NODE_ENV === "development" ? error.stack : undefined,
	};
};

/**
 * Wrapper for saga API calls with abort controller support
 * Handles common patterns: abort logic, cancellation, and error handling
 *
 * @param {Function} apiCall - The API function to call
 * @param {Array} apiArgs - Arguments to pass to the API function
 * @param {Function} successAction - Redux action creator for success
 * @param {Function} failureAction - Redux action creator for failure
 * @param {string} operationName - Name of the operation (for logging)
 * @returns {Generator} Saga generator
 */
export function* callWithAbort(apiCall, apiArgs, successAction, failureAction, operationName = "API call") {
	const abortController = new AbortController();

	try {
		const response = yield call(apiCall, ...apiArgs, abortController.signal);
		yield put(successAction(response));
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			if (process.env.NODE_ENV === "development") {
				logger.info(`🚫 ${operationName} - Request cancelled`);
			}
			return null;
		}

		if (process.env.NODE_ENV === "development") {
			logger.error(`❌ ${operationName} - Error:`, error);
		}

		yield put(failureAction(formatSagaError(error)));
		throw error;
	} finally {
		if (yield cancelled()) {
			if (process.env.NODE_ENV === "development") {
				logger.info(`🛑 ${operationName} - Saga cancelled, aborting request`);
			}
			abortController.abort();
		}
	}
}
